import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCartStore, lineUnitWithModifiers } from '../stores/cart.store.js'
import { useSessionStore } from '../stores/session.store.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { addOrderItem, createOrder, payOrderDemo } from '../lib/api.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { StadiumSeatForm } from '../components/StadiumSeatForm.js'
import {
  DEMO_PAYMENT,
  DEMO_PAYMENT_DELAY_MS,
  formatSeat,
  isSeatComplete,
  itemNotesWithSeat,
  useSeatStore,
} from '../lib/stadium.js'

/**
 * MODO ESTADIO — Resumen del pedido + pago (una sola pantalla, como la
 * referencia "Paga"). Sólo se monta con el modo estadio activo y en QR para
 * llevar; el carrito normal (Cart) no cambia.
 *
 * Pago: DEMO_PAYMENT. No hay pasarela: "Pagar ahora" simula el proceso
 * (DEMO_PAYMENT_DELAY_MS) y el servidor deja el pedido pagado como un prepago
 * real (POST /orders/:id/demo-payment, habilitado sólo para los negocios de la
 * demo). La ubicación viaja en la nota de cada ítem ("Entregar en …"), así la
 * ven Entrantes, el KDS, la comanda y el panel de entrega sin columnas nuevas.
 */
type PayOption = 'card' | 'apple' | 'google'

const PAY_OPTIONS: { key: PayOption; label: string; hint: string }[] = [
  { key: 'card', label: 'Tarjeta de crédito o débito', hint: 'Demo •••• 4242' },
  { key: 'apple', label: 'Apple Pay', hint: 'Confirma con Face ID' },
  { key: 'google', label: 'Google Pay', hint: 'Confirma en tu teléfono' },
]

/** Logotipos dibujados (sin imágenes externas ni marcas registradas en archivo). */
function PayLogo({ kind }: { kind: PayOption }) {
  if (kind === 'card') {
    return (
      <span className="st-payopt__logo st-payopt__logo--card" aria-hidden>
        <svg viewBox="0 0 32 22" width="32" height="22"><rect x="0.5" y="0.5" width="31" height="21" rx="4" fill="#0F2B3A" stroke="#2BD46A" /><rect x="0" y="5" width="32" height="4" fill="#2BD46A" /><rect x="4" y="14" width="10" height="3" rx="1.5" fill="#9FB3BD" /></svg>
      </span>
    )
  }
  if (kind === 'apple') {
    return (
      <span className="st-payopt__logo st-payopt__logo--apple" aria-hidden>
        <svg viewBox="0 0 14 17" width="12" height="15"><path fill="#fff" d="M11.6 9c0-2 1.7-3 1.8-3-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.8-2.8-.7C3 4.5 1.7 5.3 1 6.6c-1.5 2.6-.4 6.4 1 8.5.7 1 1.5 2.1 2.6 2.1 1-.1 1.4-.7 2.700-.7 1.2 0 1.6.7 2.700.6 1.1 0 1.8-1 2.500-2.100.8-1.200 1.100-2.300 1.100-2.400 0 0-2.100-.8-2.100-3.200zM9.600 3c.6-.7 1-1.700.9-2.700-.9 0-1.900.6-2.500 1.300-.5.600-1 1.600-.9 2.600 1 .1 1.900-.5 2.500-1.200z"/></svg>
        <b>Pay</b>
      </span>
    )
  }
  return (
    <span className="st-payopt__logo st-payopt__logo--google" aria-hidden>
      <b><i style={{ color: '#4285F4' }}>G</i></b> <b>Pay</b>
    </span>
  )
}

type Stage = 'idle' | 'creating' | 'paying' | 'approved'

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

export function StadiumCheckout() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const lines = useCartStore((s) => s.lines)
  const subtotal = useCartStore((s) => s.subtotalCents())
  const setQuantity = useCartStore((s) => s.setQuantity)
  const clear = useCartStore((s) => s.clear)
  const addActiveOrder = useActiveOrderStore((s) => s.add)
  const seat = useSeatStore((s) => (slug ? s.bySlug[slug] ?? null : null))
  const setSeat = useSeatStore((s) => s.setSeat)

  const [editingSeat, setEditingSeat] = useState(false)
  const [pay, setPay] = useState<PayOption>('card')
  const [stage, setStage] = useState<Stage>('idle')
  const [error, setError] = useState<string | null>(null)
  /** Pedido ya creado en un intento anterior (reintento = mismo pedido). */
  const createdOrder = useRef<{ id: string; pickupCode: string | null } | null>(null)
  /** Líneas ya agregadas a ese pedido (un reintento no las duplica). */
  const addedLines = useRef<Set<string>>(new Set())
  const nonce = useRef<string | null>(null)

  if (!session) {
    navigate(`/r/${slug}`, { replace: true })
    return <></>
  }

  const seatOk = isSeatComplete(seat)
  const showSeatForm = editingSeat || !seatOk
  const busy = stage !== 'idle'
  const count = lines.reduce((a, l) => a + l.quantity, 0)

  async function payNow(): Promise<void> {
    if (!session || busy || lines.length === 0) return
    if (!isSeatComplete(seat)) {
      setEditingSeat(true)
      setError('Antes de pagar, dinos dónde estás sentado.')
      return
    }
    setError(null)
    setStage('creating')
    const started = Date.now()

    // 1) Pedido PARA LLEVAR (mismo endpoint que el guest normal). Nace
    //    PENDING_ACCEPTANCE; el pago de demostración lo cierra y lo manda a cocina.
    if (!createdOrder.current) {
      if (!nonce.current) nonce.current = crypto.randomUUID()
      const created = await createOrder(session.token, {
        tenantId: session.tenantId,
        tableSessionId: session.tableSessionId,
        type: 'TAKEOUT',
        chosenPaymentMethod: 'ONLINE',
        idempotencyKey: nonce.current,
      })
      if (!created.ok) {
        setStage('idle')
        setError(`No pudimos crear tu pedido (${created.error.message}). Intenta de nuevo.`)
        return
      }
      createdOrder.current = { id: created.data.id, pickupCode: created.data.pickupCode ?? null }
      addedLines.current = new Set()
    }
    const orderId = createdOrder.current.id

    // 2) Ítems, cada uno con la ubicación en su nota.
    for (const l of lines) {
      if (addedLines.current.has(l.lineId)) continue
      const r = await addOrderItem(session.token, orderId, {
        productId: l.productId,
        name: l.productName,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        ...(l.modifiers.length > 0 ? { modifiers: l.modifiers } : {}),
        notes: itemNotesWithSeat(l.notes, seat),
      })
      if (!r.ok) {
        setStage('idle')
        setError(`No pudimos agregar ${l.productName} (${r.error.message}). Toca "Pagar ahora" para reintentar.`)
        return
      }
      addedLines.current.add(l.lineId)
    }

    addActiveOrder({
      orderId,
      tableSessionId: session.tableSessionId,
      pickupCode: createdOrder.current.pickupCode,
      placedAt: Date.now(),
    })

    // 3) PAGO DE DEMOSTRACIÓN: animación de ~1.5 s + confirmación del servidor.
    setStage('paying')
    const [paid] = await Promise.all([
      payOrderDemo(session.token, orderId),
      sleep(Math.max(0, DEMO_PAYMENT_DELAY_MS - (Date.now() - started))),
    ])
    if (!paid.ok) {
      setStage('idle')
      setError(
        paid.error.code === 'FORBIDDEN'
          ? 'Este negocio no tiene activo el pago de demostración. Tu pedido quedó registrado: puedes verlo y pagarlo al recibirlo.'
          : `No se pudo confirmar el pago (${paid.error.message}). Toca "Pagar ahora" para reintentar.`,
      )
      return
    }
    setStage('approved')
    await sleep(700)
    clear()
    createdOrder.current = null
    nonce.current = null
    navigate(`/r/${slug}/order/${orderId}`, { replace: true })
  }

  return (
    <div className="app st-app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} branchId={session.branchId} />
      <div className="container st-pad-bottom">
        <div className="st-topbar">
          <Link to={`/r/${slug}/menu`} className="st-back" aria-label="Seguir ordenando">←</Link>
          <h1 className="st-topbar__title">Resumen del pedido</h1>
        </div>

        <div className="st-card">
          {lines.length === 0 && (
            <div className="st-empty">
              <p className="st-muted">Tu carrito está vacío.</p>
              <Link to={`/r/${slug}/menu`} className="st-btn st-btn--primary">Ver el menú</Link>
            </div>
          )}
          {lines.map((l) => (
            <div key={l.lineId} className="st-line">
              <div className="st-line__body">
                <div className="st-line__name">{l.productName}</div>
                {l.modifiers.length > 0 && (
                  <div className="st-line__meta">{l.modifiers.map((m) => m.name).join(' · ')}</div>
                )}
                {l.notes && <div className="st-line__meta">{l.notes}</div>}
                <div className="st-line__price">{formatMoney(lineUnitWithModifiers(l) * l.quantity)}</div>
              </div>
              <div className="st-stepper" aria-label={`Cantidad de ${l.productName}`}>
                <button type="button" disabled={busy} onClick={() => setQuantity(l.lineId, l.quantity - 1)} aria-label="Quitar uno">−</button>
                <span>{l.quantity}</span>
                <button type="button" disabled={busy} onClick={() => setQuantity(l.lineId, l.quantity + 1)} aria-label="Agregar uno">+</button>
              </div>
            </div>
          ))}
          {lines.length > 0 && (
            <div className="st-totals">
              <div className="st-totals__row"><span>Subtotal ({count} {count === 1 ? 'producto' : 'productos'})</span><span>{formatMoney(subtotal)}</span></div>
              <div className="st-totals__row"><span>Entrega a tu asiento</span><span className="st-free">Gratis</span></div>
              <div className="st-totals__row st-totals__row--total"><span>Total</span><span>{formatMoney(subtotal)}</span></div>
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <div className="st-card">
            <div className="st-card__head">
              <h2 className="st-card__title">Entregar en</h2>
              {seatOk && !editingSeat && (
                <button type="button" className="st-link" disabled={busy} onClick={() => setEditingSeat(true)}>Cambiar</button>
              )}
            </div>
            {showSeatForm ? (
              <StadiumSeatForm
                initial={seat}
                askName={false}
                submitLabel="Guardar ubicación"
                onSubmit={(v) => {
                  if (!slug) return
                  setSeat(slug, { ...v, ...(seat?.name ? { name: seat.name } : {}) })
                  setEditingSeat(false)
                  setError(null)
                }}
                {...(seatOk ? { onCancel: () => setEditingSeat(false) } : {})}
              />
            ) : (
              <div className="st-seat-view">
                <span className="st-seat-view__pin" aria-hidden>📍</span>
                <div>
                  <div className="st-seat-view__main">{formatSeat(seat!)}</div>
                  <div className="st-muted">Te lo llevamos hasta tu lugar.</div>
                </div>
              </div>
            )}
          </div>
        )}

        {lines.length > 0 && (
          <div className="st-card">
            <h2 className="st-card__title">Método de pago</h2>
            <div className="st-paylist" role="radiogroup" aria-label="Método de pago">
              {PAY_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={pay === o.key}
                  disabled={busy}
                  className={`st-payopt${pay === o.key ? ' is-on' : ''}`}
                  onClick={() => setPay(o.key)}
                >
                  <PayLogo kind={o.key} />
                  <span className="st-payopt__body">
                    <span className="st-payopt__label">{o.label}</span>
                    <span className="st-payopt__hint">{o.hint}</span>
                  </span>
                  <span className="st-radio" aria-hidden />
                </button>
              ))}
            </div>
            {DEMO_PAYMENT && (
              <p className="st-demo-note">
                <strong>Pago de demostración.</strong> No se hace ningún cargo: cada negocio conecta
                su propia pasarela con su banco.
              </p>
            )}
          </div>
        )}

        {error && <div className="st-alert" role="alert">{error}</div>}
        {error && createdOrder.current && (
          <Link to={`/r/${slug}/order/${createdOrder.current.id}`} className="st-btn st-btn--ghost">
            Ver mi pedido
          </Link>
        )}
      </div>

      {lines.length > 0 && (
        <div className="st-cartbar">
          <button
            type="button"
            className="st-btn st-btn--primary st-cartbar__btn"
            disabled={busy || lines.length === 0}
            onClick={() => void payNow()}
          >
            <span>Pagar ahora</span>
            <span className="st-cartbar__meta">{formatMoney(subtotal)}</span>
          </button>
        </div>
      )}

      {busy && (
        <div className="st-overlay" role="status" aria-live="polite">
          <div className="st-overlay__card">
            {stage === 'approved' ? (
              <>
                <div className="st-check" aria-hidden>✓</div>
                <div className="st-overlay__title">¡Pago aprobado!</div>
                <div className="st-muted">El local ya recibió tu pedido.</div>
              </>
            ) : (
              <>
                <span className="st-ring" aria-hidden />
                <div className="st-overlay__title">
                  {stage === 'creating' ? 'Enviando tu pedido…' : 'Procesando pago…'}
                </div>
                <div className="st-muted">
                  {pay === 'card' ? 'Tarjeta •••• 4242' : pay === 'apple' ? 'Apple Pay' : 'Google Pay'} · demostración
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
